<script setup lang="ts">
import { computed } from 'vue'
import { useObjectUrl } from '@vueuse/core'

/**
 * An image with a box around it — from a URL (`src`) or straight from a picked `File`,
 * whose object URL is created and revoked here, so a form can preview a file it has not
 * sent yet.
 *
 * With neither, the `placeholder` slot is drawn, so the box never collapses while a record
 * is on its way. For a logo prefer `block`, which spans the row and takes its height from
 * the image: a fixed box rarely matches a logo's proportions, and `fit` defaults to
 * `contain` for the same reason — nothing is cropped.
 */
const props = withDefaults(
  defineProps<{
    src?: string | null
    file?: File | null
    alt?: string
    width?: string | number
    height?: string | number
    block?: boolean
    /** Upper bound for the auto height in `block` mode. */
    maxHeight?: string | number
    /**
     * object-fit. Defaults to `contain`: every preview in this app is a logo, and a
     * fixed box almost never matches its proportions, so `cover` cropped the middle
     * out of wide logos. Pass `cover` explicitly for a thumbnail that should fill.
     */
    fit?: 'cover' | 'contain' | 'fill' | 'none'
    rounded?: boolean | 'circle'
    /** Drop the grey ground, which is there to make a row of logos line up: an image
     * with its own transparency reads as a grey tile against it. */
    transparent?: boolean
  }>(),
  {
    fit: 'contain',
    rounded: false,
    block: false,
    maxHeight: 160,
    transparent: false
  }
)

const objectUrlRef = useObjectUrl(() => props.file)

const previewSrc = computed(() => {
  if (props.src) return props.src
  return objectUrlRef.value
})

/**
 * A static `width="120"` reaches the component as the string `'120'`, and `width: 120`
 * is invalid CSS — the box then collapses to 0x0 and neither the image nor the
 * placeholder is visible. Anything that is only digits gets the missing unit.
 */
function toCssSize(value: string | number | undefined) {
  if (value == null || value === '') return undefined
  return /^\d+(\.\d+)?$/.test(String(value)) ? `${value}px` : String(value)
}

const borderRadius = computed(() =>
  props.rounded === 'circle'
    ? '50%'
    : props.rounded
      ? 'var(--el-border-radius-base, 4px)'
      : undefined
)

/** In `block` mode the height comes from the image, so the box must not pin it. */
const autoHeight = computed(() => props.block && props.height == null)

const wrapperStyle = computed(() => ({
  width: props.block ? '100%' : toCssSize(props.width),
  height: autoHeight.value ? undefined : toCssSize(props.height)
}))

const imageStyle = computed(() => ({
  // `max-width` rather than `width` on purpose: the image fills the row when it is
  // wide enough and keeps its natural size when it is not. Forcing `width: 100%`
  // would upscale a small logo into a blurry banner.
  width: props.block ? 'auto' : toCssSize(props.width),
  maxWidth: props.block ? '100%' : undefined,
  height: autoHeight.value ? 'auto' : toCssSize(props.height),
  maxHeight: autoHeight.value ? toCssSize(props.maxHeight) : undefined,
  objectFit: props.fit,
  borderRadius: borderRadius.value
}))

defineSlots<{
  placeholder?: () => unknown
}>()
</script>

<template>
  <div
    class="image-preview"
    :class="{
      'image-preview--block': block,
      'image-preview--transparent': transparent
    }"
    :style="wrapperStyle"
  >
    <img
      v-if="previewSrc"
      :src="previewSrc"
      :alt="alt ?? ''"
      class="image-preview__img"
      :style="imageStyle"
    />
    <div v-else class="image-preview__placeholder">
      <slot name="placeholder">
        <span class="image-preview__placeholder-text">No image</span>
      </slot>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.image-preview {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--el-fill-color-light, #f5f7fa);
}

/* Height follows the image, so there is nothing to centre against and no backdrop
   to show through — the box hugs the picture and sits with the rest of the form. */
.image-preview--block {
  display: flex;
  justify-content: flex-start;
  background: transparent;
}

.image-preview--transparent {
  background: transparent;
}

.image-preview__img {
  display: block;
  max-width: 100%;
  max-height: 100%;
}

.image-preview__placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 80px;
  color: var(--el-text-color-placeholder, #a8abb2);
  font-size: 14px;
}

.image-preview__placeholder-text {
  font-size: inherit;
  color: inherit;
}
</style>
